import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { TierForm, normalizeTier } from '../components/TierForm';
import { ArrowLeft, Plus, X, Edit2, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PassTiers({ user }) {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newTier, setNewTier] = useState({
    id: '',
    tier_name: '',
    price: 0,
    quantity_available: 0,
    attendees_per_pass: 1,
    description: '',
    is_comp: false,
    status_default: 'active',
    type: 'entry',
    allow_quantity_selection: true,
    min_quantity: '',
    max_quantity: ''
  });
  const [editTier, setEditTier] = useState(null);

  useEffect(() => {
    fetchPassTiers();
  }, []);

  const fetchPassTiers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/users/pass-tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiers(response.data);
    } catch (error) {
      toast.error('Failed to fetch pass tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newTier.tier_name || newTier.price < 0) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      const token = localStorage.getItem('session_token');
      const tierToAdd = {
        ...normalizeTier(newTier),
        id: `tier_${Date.now()}`,
      };
      await axios.post(`${API}/users/pass-tiers`, tierToAdd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Pass tier template added');
      setNewTier({ id: '', tier_name: '', price: 0, quantity_available: 0, attendees_per_pass: 1, description: '', is_comp: false, status_default: 'active', type: 'entry', allow_quantity_selection: true, min_quantity: '', max_quantity: '' });
      setShowAdd(false);
      fetchPassTiers();
    } catch (error) {
      toast.error('Failed to add pass tier');
    }
  };

  const handleEdit = (tier) => {
    setEditingId(tier.id);
    setEditTier({ ...tier });
  };

  const handleSaveEdit = async () => {
    if (!editTier.tier_name) { toast.error('Tier name required'); return; }
    try {
      const token = localStorage.getItem('session_token');
      const updated = normalizeTier(editTier);
      await axios.put(`${API}/users/pass-tiers/${editTier.id}`, updated, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success('Pass tier updated');
      setEditingId(null);
      setEditTier(null);
      fetchPassTiers();
    } catch (error) {
      toast.error('Failed to update pass tier');
    }
  };

  const handleDelete = async (tierId) => {
    if (!confirm('Delete this pass tier template?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/users/pass-tiers/${tierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pass tier template deleted');
      fetchPassTiers();
    } catch (error) {
      toast.error('Failed to delete pass tier');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="mb-4 -ml-2 text-[#EAE7E1] hover:text-[#D4AF37]" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-[#EAE7E1] tracking-tight">Saved Pass Tiers</h1>
          <p className="mt-1 text-base text-[#AFAFAF]">Create reusable pass tier templates for quick event setup</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-[#EAE7E1]">Your Pass Tier Templates</h2>
              <button type="button" onClick={() => navigate('/guide#pass-tiers')} className="text-[11px] text-[#D4AF37] hover:underline" data-testid="help-pass-tiers">What does this mean?</button>
            </div>
            <p className="text-sm text-[#AFAFAF] mt-1">VIP, General Admission, Couples, Family Pack, etc.</p>
          </div>
          <Button onClick={() => setShowAdd(true)} className="bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold" data-testid="new-tier-button">
            <Plus className="w-4 h-4 mr-2" />
            New Tier
          </Button>
        </div>

        {showAdd && (
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6 mb-6" data-testid="add-tier-form">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-[#EAE7E1]">Create Pass Tier Template</h3>
              <Button onClick={() => setShowAdd(false)} variant="ghost" size="sm" className="text-[#AFAFAF]">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <TierForm tier={newTier} onChange={setNewTier} showTypeSelector={true} />
            <Button onClick={handleAdd} className="w-full mt-4 bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm py-3 font-bold" data-testid="save-tier-button">
              Save Template
            </Button>
          </div>
        )}

        {tiers.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-12 text-center" data-testid="empty-tiers">
            <div className="text-[#AFAFAF] mb-4">No saved pass tier templates yet</div>
            <p className="text-sm text-[#AFAFAF] mb-6">Create templates like "VIP $100", "Couple $150" to reuse across events</p>
            <Button onClick={() => setShowAdd(true)} variant="outline" className="border-[#D4AF37] text-[#D4AF37]">
              Create Your First Tier
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tiers.map((tier) => (
              <div key={tier.id} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6 hover:border-[#D4AF37]/50" data-testid={`tier-card-${tier.id}`}>
                {editingId === tier.id ? (
                  <div className="space-y-3">
                    <TierForm tier={editTier} onChange={setEditTier} showTypeSelector={true} />
                    <div className="flex space-x-2 pt-2">
                      <Button onClick={handleSaveEdit} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold flex-1" data-testid="save-edit-button">
                        <Save className="w-4 h-4 mr-2" /> Save
                      </Button>
                      <Button onClick={() => { setEditingId(null); setEditTier(null); }} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm flex-1" data-testid="cancel-edit-button">
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${tier.type === 'addon' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#1A1A1A] text-[#AFAFAF]'}`}>{tier.type === 'addon' ? 'Add-On' : 'Entry'}</span>
                          <h3 className="text-lg font-bold text-[#EAE7E1]">{tier.tier_name}</h3>
                        </div>
                        {tier.is_comp ? (
                          <div className="text-[#16A34A] font-bold text-lg">Complimentary</div>
                        ) : (
                          <div className="text-[#D4AF37] font-bold text-2xl">${tier.price}</div>
                        )}
                      </div>
                      <div className="flex space-x-1">
                        <Button onClick={() => handleEdit(tier)} variant="ghost" size="sm" className="text-[#AFAFAF] hover:text-[#D4AF37]" data-testid={`edit-tier-${tier.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => handleDelete(tier.id)} variant="ghost" size="sm" className="text-[#AFAFAF] hover:text-[#E11D48]" data-testid={`delete-tier-${tier.id}`}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-[#AFAFAF]">
                        <span>Quantity:</span>
                        <span className="text-[#EAE7E1]">{tier.quantity_available || 'Unlimited'}</span>
                      </div>
                      {(tier.type || 'entry') === 'entry' && (
                        <div className="flex justify-between text-[#AFAFAF]">
                          <span>Attendees per pass:</span>
                          <span className="text-[#EAE7E1]">{tier.attendees_per_pass}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-[#AFAFAF]">
                        <span>Qty selection:</span>
                        <span className={tier.allow_quantity_selection === false ? 'text-[#FFB800]' : 'text-[#EAE7E1]'}>
                          {tier.allow_quantity_selection === false ? 'Fixed (1)' : 'Adjustable'}
                          {tier.allow_quantity_selection !== false && tier.min_quantity ? ` (min ${tier.min_quantity})` : ''}
                          {tier.allow_quantity_selection !== false && tier.max_quantity ? ` (max ${tier.max_quantity})` : ''}
                        </span>
                      </div>
                      {tier.description && (
                        <div className="text-[#AFAFAF] italic mt-3">{tier.description}</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
